/**
 * Date Formatting Utilities
 * Standardized DD/MM/YYYY format for Indian operations
 */

/**
 * Format date to DD/MM/YYYY string
 * @param {Date|string} date - Date object or ISO string
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  const { 
    includeTime = false,
    includeWeekday = false,
    shortMonth = false,
    longMonth = false
  } = options;
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  
  // Short month name (Jan, Feb, etc.)
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const longMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const shortWeekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  let result = '';
  
  if (includeWeekday) {
    result += shortWeekdays[d.getDay()] + ', ';
  }
  
  if (longMonth) {
    result += `${d.getDate()} ${longMonthNames[d.getMonth()]} ${year}`;
  } else if (shortMonth) {
    result += `${d.getDate()} ${monthNames[d.getMonth()]} ${year}`;
  } else {
    result += `${day}-${month}-${year}`;
  }
  
  if (includeTime) {
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    result += ` ${hours}:${minutes}`;
  }
  
  return result;
};

/**
 * Format date for display with smart relative formatting
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string with relative context
 */
export const formatRelativeDate = (date) => {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.floor((targetDate - today) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1 && diffDays <= 7) return `In ${diffDays} days`;
  if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;
  
  return formatDate(d);
};

/**
 * Format date for input[type="date"] (YYYY-MM-DD)
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} YYYY-MM-DD format
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Parse DD/MM/YYYY string to Date object
 * @param {string} dateStr - DD/MM/YYYY formatted string
 * @returns {Date|null} Date object or null if invalid
 */
export const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;
  
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;
  
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);
  
  const date = new Date(year, month, day);
  if (isNaN(date.getTime())) return null;
  
  return date;
};

/**
 * Format timestamp for activity/chat display
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Smart formatted timestamp
 */
export const formatTimestamp = (date) => {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return formatDate(d);
};

/**
 * Format date for PDF exports (DD MMMM YYYY)
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date for PDF
 */
export const formatDateForPDF = (date) => {
  return formatDate(date, { longMonth: true });
};

/**
 * Get day suffix (st, nd, rd, th)
 * @param {number} day - Day of month
 * @returns {string} Day suffix
 */
export const getDaySuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

export default {
  formatDate,
  formatRelativeDate,
  formatDateForInput,
  parseDDMMYYYY,
  formatTimestamp,
  formatDateForPDF,
  getDaySuffix
};
